import React, { useEffect, useMemo, useRef, useState } from 'react';
import trash from '../../../../../assets/restaurant/images/trash.svg';
import { getSubCategories, addSubCategory, deleteSubCategory, editSubCategory, sortSubCategories, subCategoryDetail } from '../../../services/subCategory.services';
import { categoryDetail, categoryOptions } from '../../../services/category.services';
import { LOADER_TIMEOUT_TIME, SWAL_SETTINGS } from '../../../../../utils/Constants';
import Swal from 'sweetalert2';
import { useFormik } from 'formik';
import { useValidationSchemas } from '../../../validator/validation';
import { useTranslation } from 'react-i18next';
import { getColorType, getTranslatedKey, globalLoader, handleOffcanvasScroll, handleServerValidations, useDebouncing } from '../../../../../utils/commonfunction';
import { ReactSortable } from 'react-sortablejs';
import { Link, useParams } from 'react-router-dom';
import GridLoader from '../../../shared/gridLoader';
import RecordNotFound from '../../../shared/recordNotFound';
import CategoryCardActions from '../shared/CategoryCardActions';

export default function SubCategoriesListing() {
    const { categoryId } = useParams();
    const [sortableList, setSortableList] = useState([]);
    const [refreshList, setRefreshList] = useState(false);
    const [List, setList] = useState([]);
    const [categoryName, setCategoryName] = useState('');
    const addCloseBtn = useRef();
    const editCloseBtn = useRef();
    const sortingCloseBtn = useRef();
    const [loader, setLoader] = useState(true);
    const { t } = useTranslation();
    const { subCategoryValidationSchema } = useValidationSchemas();
    const [globalSearch, setGlobalSearch] = useState('');
    const [langChanged, setLangChanged] = useState(false);
    const [addSubmissionLoader, setAddSubmissionLoader] = useState(false);
    const [editSubmissionLoader, setEditSubmissionLoader] = useState(false);
    const [categoryList, setCategoryList] = useState([]);

    const addForm = useFormik({
        initialValues: { name: '', name_ja: '', color: '#000000', short_description: '', short_description_ja: '', category_id: categoryId || '' },
        validationSchema: subCategoryValidationSchema,
        enableReinitialize: true,
        onSubmit: (values, { resetForm }) => {
            setAddSubmissionLoader(true);
            addSubCategory({ ...values })
                .then((response) => {
                    if (response.success) {
                        Swal.fire({ icon: 'success', text: response.message, ...SWAL_SETTINGS });
                        resetForm();
                        addCloseBtn?.current?.click();
                        setRefreshList(!refreshList);
                    } else {
                        Swal.fire({ icon: 'error', text: handleServerValidations(response), ...SWAL_SETTINGS });
                    }
                    setAddSubmissionLoader(false);
                })
                .catch(() => setAddSubmissionLoader(false));
        }
    });

    const editForm = useFormik({
        initialValues: { _id: '', name: '', name_ja: '', color: '', short_description: '', short_description_ja: '', category_id: '' },
        validationSchema: subCategoryValidationSchema,
        enableReinitialize: true,
        onSubmit: (values, { resetForm }) => {
            setEditSubmissionLoader(true);
            editSubCategory({ o_id: values._id, ...values })
                .then((response) => {
                    if (response.success) {
                        Swal.fire({ icon: 'success', text: response.message, ...SWAL_SETTINGS });
                        resetForm();
                        setRefreshList(!refreshList);
                        editCloseBtn?.current?.click();
                    } else {
                        Swal.fire({ icon: 'error', text: handleServerValidations(response), ...SWAL_SETTINGS });
                    }
                    setEditSubmissionLoader(false);
                })
                .catch(() => setEditSubmissionLoader(false));
        }
    });

    const deleteItem = (item) => {
        Swal.fire({
            title: t('msg_are_you_sure'),
            text: t('btn_delete_text'),
            imageUrl: trash,
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('btn_delete'),
            cancelButtonText: t('btn_cancel'),
            reverseButtons: true,
            customClass: { popup: 'swal-restaurant', confirmButton: 'btn btn-danger col', cancelButton: 'btn btn-secondary col' },
            allowOutsideClick: false
        }).then((result) => {
            if (result.isConfirmed) {
                globalLoader(true);
                deleteSubCategory({ o_id: item._id }).then((response) => {
                    globalLoader(false);
                    if (response.success) {
                        Swal.fire({ icon: 'success', text: response.message, ...SWAL_SETTINGS });
                        setRefreshList(!refreshList);
                    } else {
                        Swal.fire({ icon: 'error', text: handleServerValidations(response), ...SWAL_SETTINGS });
                    }
                }).catch(() => globalLoader(false));
            } else {
                setSortableList(List);
            }
        });
    };

    useMemo(() => {
        document.addEventListener('language_change', function () { setLangChanged(!langChanged); });
    });

    useEffect(() => {
        categoryOptions().then((res) => {
            if (res.success) setCategoryList(res.data || []);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (categoryId) {
            categoryDetail({ o_id: categoryId }).then((res) => {
                if (res.success) setCategoryName(res.data?.name || '');
            }).catch(() => {});
        } else {
            setCategoryName('');
        }
    }, [categoryId]);

    useEffect(() => {
        addForm.setFieldValue('category_id', categoryId || '');
    }, [categoryId]);

    useEffect(() => {
        setLoader(true);
        const formData = new FormData();
        formData.append('global_search', globalSearch);
        if (categoryId) formData.append('category_id', categoryId);
        getSubCategories(formData).then(data => {
            const list = data?.data?.list || [];
            setList(list);
            setSortableList(list);
            setTimeout(() => setLoader(false), LOADER_TIMEOUT_TIME);
        }).catch(() => setTimeout(() => setLoader(false), LOADER_TIMEOUT_TIME));
    }, [refreshList, globalSearch, langChanged, categoryId]);

    const prepareGlobalSearch = (value) => setGlobalSearch(value);
    const searchDebounce = useDebouncing(prepareGlobalSearch);

    const getDetail = (item) => {
        subCategoryDetail({ o_id: item._id }).then((response) => {
            if (response.success) {
                editForm.setValues({
                    _id: item._id,
                    name: response.data?.name || '',
                    name_ja: response.data?.name_ja || '',
                    color: response.data?.color || '',
                    short_description: response.data?.short_description || '',
                    short_description_ja: response.data?.short_description_ja || '',
                    category_id: response.data?.category_id || ''
                });
            } else {
                Swal.fire({ icon: 'error', text: handleServerValidations(response), ...SWAL_SETTINGS });
            }
        }).catch(console.error);
    };

    function handleSort(closeModal = true) {
        sortSubCategories({ sorted_array: sortableList }).then((response) => {
            if (response.success) {
                Swal.fire({ icon: 'success', text: response.message, ...SWAL_SETTINGS });
                if (closeModal) sortingCloseBtn?.current?.click();
                setRefreshList(!refreshList);
            } else {
                Swal.fire({ icon: 'error', text: handleServerValidations(response), ...SWAL_SETTINGS });
            }
        }).catch(console.error);
    }

    function isSortingChanged() {
        for (let i = 0; i < List.length; i++) {
            if (List[i]?.order !== sortableList[i]?.order) return true;
        }
        return false;
    }

    const handleCloseSorting = () => {
        if (isSortingChanged()) {
            Swal.fire({
                title: t('question_to_save_data'),
                text: t('confirm_heading_found_unsaved_data_description'),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#6c757d',
                cancelButtonColor: '#dc3545',
                confirmButtonText: t('btn_text_save_changes'),
                cancelButtonText: t('btn_text_discard'),
                customClass: { popup: 'swal-restaurant', confirmButton: 'btn btn-secondary col', cancelButton: 'btn btn-danger col' },
                allowOutsideClick: false
            }).then((result) => {
                if (result.isConfirmed) handleSort(false);
                else setSortableList(List);
            });
        }
    };

    return (
        <main id='main-content'>
            <div className='inner-container'>
                <div className='page-header d-flex align-items-center'>
                    <h4 className='heading-title-text'>
                        {categoryName ? `${categoryName} - ` : ''}{t('label_sub_categories')} ({List.length})
                    </h4>
                </div>
                <div className='card-outer-header d-flex justify-content-between align-items-center mb-4'>
                    <div className='search-box'>
                        <input type='text' className='form-control search-input' placeholder={t('input_placeholder_search')} onInput={(e) => searchDebounce(e.target.value)} />
                    </div>
                    <div className='card-head-group d-flex align-items-center gap-2'>
                        <button className='btn btn-primary' data-bs-toggle='modal' data-bs-target='#sort-sub-modal'>{t('label_sort_sub_category')}</button>
                        <button className='btn btn-primary' data-bs-toggle='offcanvas' data-bs-target='#add-sub-category'><i className='ti ti-plus'></i> {t('label_create_sub_category')}</button>
                    </div>
                </div>
                <div className='dashboard-content-wraper'>
                    <div className='category-list-wrap'>
                        {loader ? <GridLoader /> : List.length ? (
                            <div className='category-list-grid'>
                                {List.map((item, index) => (
                                    <div key={index} className={'category-box ' + (`category_bg_${getColorType(item.color)}`)} style={{ background: item.color }}>
                                        <CategoryCardActions
                                            viewTarget="#view-sub-category"
                                            editTarget="#edit-sub-category"
                                            onView={() => getDetail(item)}
                                            onEdit={() => getDetail(item)}
                                            onDelete={() => deleteItem(item)}
                                        >
                                            <span>{getTranslatedKey('name', item)}</span>
                                        </CategoryCardActions>
                                        <div className='category-item-info'>
                                            <Link className='text-white' to={`/restaurant/menu/menu-item/list?sub_category_id=${item?._id}`}>{item?.items_count} {t('label_items')}</Link>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : <RecordNotFound heading='no_sub_category_found_text' text='' />}
                    </div>
                </div>
            </div>

            {/* Edit offcanvas */}
            <div className='offcanvas offcanvas-end right-modal-w' id='edit-sub-category' data-bs-backdrop='static' tabIndex='-1'>
                <div className='offcanvas-header'>
                    <h5 className='offcanvas-title'>{t('label_edit_sub_category')}</h5>
                    <button type='button' className='btn btn-icon ms-auto' ref={editCloseBtn} data-bs-dismiss='offcanvas' aria-label='Close' onClick={() => editForm.resetForm()}>
                        <i className='ti ti-x'></i>
                    </button>
                </div>
                <div className='offcanvas-body' onScroll={(e) => handleOffcanvasScroll(e, 'edit-sub-category')}>
                    <form onSubmit={editForm.handleSubmit}>
                        <input type='hidden' name='_id' value={editForm.values._id} />
                        <div className='mb-3'>
                            <label className='form-label'>{t('label_categories')}<span>*</span></label>
                            <select className='form-control' name='category_id' value={editForm.values.category_id} onChange={editForm.handleChange} onBlur={editForm.handleBlur}>
                                <option value=''>{t('input_placeholder_select')}</option>
                                {categoryList.map((cat) => (
                                    <option key={cat._id} value={cat._id}>{cat.name}</option>
                                ))}
                            </select>
                            {editForm.touched.category_id && editForm.errors.category_id ? <div className='text-danger'>{t(editForm.errors.category_id)}</div> : null}
                        </div>
                        <div className='mb-3'>
                            <label className='form-label'>{t('label_sub_category_name_english')}<span>*</span></label>
                            <input type='text' className='form-control' placeholder={t('sub_category_name_english_placeholder')} name='name' value={editForm.values.name} onChange={editForm.handleChange} onBlur={editForm.handleBlur} />
                            {editForm.touched.name && editForm.errors.name ? <div className='text-danger'>{t(editForm.errors.name)}</div> : null}
                        </div>
                        <div className='mb-3'>
                            <label className='form-label'>{t('label_sub_category_name_japanese')}<span>*</span></label>
                            <input type='text' className='form-control' placeholder={t('sub_category_name_japanese_placeholder')} name='name_ja' value={editForm.values.name_ja} onChange={editForm.handleChange} onBlur={editForm.handleBlur} />
                            {editForm.touched.name_ja && editForm.errors.name_ja ? <div className='text-danger'>{t(editForm.errors.name_ja)}</div> : null}
                        </div>
                        <div className='mb-3 input-type-color'>
                            <label className='form-label'>{t('label_color')}<span>*</span></label>
                            <div className='input-type-color-box'>
                                <input type='text' className='form-control' name='color' value={editForm.values.color} onChange={editForm.handleChange} onBlur={editForm.handleBlur} />
                                <input type='color' name='color' className='cp' value={editForm.values.color} onChange={editForm.handleChange} onBlur={editForm.handleBlur} />
                            </div>
                            {editForm.touched.color && editForm.errors.color ? <div className='text-danger'>{t(editForm.errors.color)}</div> : null}
                        </div>
                        <div className='mb-3'>
                            <label className='form-label'>{t('label_short_description')}<span>*</span></label>
                            <textarea className='form-control' style={{ height: '120px' }} name='short_description' value={editForm.values.short_description} onChange={editForm.handleChange} onBlur={editForm.handleBlur} />
                            {editForm.touched.short_description && editForm.errors.short_description ? <div className='text-danger'>{t(editForm.errors.short_description)}</div> : null}
                        </div>
                        <div className='mb-3'>
                            <label className='form-label'>{t('label_short_description_ja')}<span>*</span></label>
                            <textarea className='form-control' style={{ height: '120px' }} name='short_description_ja' value={editForm.values.short_description_ja} onChange={editForm.handleChange} onBlur={editForm.handleBlur} />
                            {editForm.touched.short_description_ja && editForm.errors.short_description_ja ? <div className='text-danger'>{t(editForm.errors.short_description_ja)}</div> : null}
                        </div>
                        <div className='button-form'>
                            <button type='submit' className={'btn btn-primary w-100' + (editSubmissionLoader ? ' btn-loader' : '')}>{t('btn_update')}</button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Add offcanvas */}
            <div className='offcanvas offcanvas-end right-modal-w' id='add-sub-category' data-bs-backdrop='static' tabIndex='-1'>
                <div className='offcanvas-header'>
                    <h5 className='offcanvas-title'>{t('label_create_sub_category')}</h5>
                    <button type='button' className='btn btn-icon ms-auto' ref={addCloseBtn} data-bs-dismiss='offcanvas' aria-label='Close' onClick={() => addForm.resetForm()}>
                        <i className='ti ti-x'></i>
                    </button>
                </div>
                <div className='offcanvas-body' onScroll={(e) => handleOffcanvasScroll(e, 'add-sub-category')}>
                    <form onSubmit={addForm.handleSubmit}>
                        <div className='mb-3'>
                            <label className='form-label'>{t('label_categories')}<span>*</span></label>
                            <select className='form-control' name='category_id' value={addForm.values.category_id} onChange={addForm.handleChange} onBlur={addForm.handleBlur}>
                                <option value=''>{t('input_placeholder_select')}</option>
                                {categoryList.map((cat) => (
                                    <option key={cat._id} value={cat._id}>{cat.name}</option>
                                ))}
                            </select>
                            {addForm.touched.category_id && addForm.errors.category_id ? <div className='text-danger'>{t(addForm.errors.category_id)}</div> : null}
                        </div>
                        <div className='mb-3'>
                            <label className='form-label'>{t('label_sub_category_name_english')}<span>*</span></label>
                            <input type='text' className='form-control' placeholder={t('sub_category_name_english_placeholder')} name='name' value={addForm.values.name} onChange={addForm.handleChange} onBlur={addForm.handleBlur} />
                            {addForm.touched.name && addForm.errors.name ? <div className='text-danger'>{t(addForm.errors.name)}</div> : null}
                        </div>
                        <div className='mb-3'>
                            <label className='form-label'>{t('label_sub_category_name_japanese')}<span>*</span></label>
                            <input type='text' className='form-control' placeholder={t('sub_category_name_japanese_placeholder')} name='name_ja' value={addForm.values.name_ja} onChange={addForm.handleChange} onBlur={addForm.handleBlur} />
                            {addForm.touched.name_ja && addForm.errors.name_ja ? <div className='text-danger'>{t(addForm.errors.name_ja)}</div> : null}
                        </div>
                        <div className='mb-3 input-type-color'>
                            <label className='form-label'>{t('label_color')}<span>*</span></label>
                            <div className='input-type-color-box'>
                                <input type='text' className='form-control' name='color' value={addForm.values.color} onChange={addForm.handleChange} onBlur={addForm.handleBlur} />
                                <input type='color' name='color' className='cp' value={addForm.values.color} onChange={addForm.handleChange} onBlur={addForm.handleBlur} />
                            </div>
                            {addForm.touched.color && addForm.errors.color ? <div className='text-danger'>{t(addForm.errors.color)}</div> : null}
                        </div>
                        <div className='mb-3'>
                            <label className='form-label'>{t('label_short_description')}<span>*</span></label>
                            <textarea className='form-control' style={{ height: '120px' }} name='short_description' value={addForm.values.short_description} onChange={addForm.handleChange} onBlur={addForm.handleBlur} />
                            {addForm.touched.short_description && addForm.errors.short_description ? <div className='text-danger'>{t(addForm.errors.short_description)}</div> : null}
                        </div>
                        <div className='mb-3'>
                            <label className='form-label'>{t('label_short_description_ja')}<span>*</span></label>
                            <textarea className='form-control' style={{ height: '120px' }} name='short_description_ja' value={addForm.values.short_description_ja} onChange={addForm.handleChange} onBlur={addForm.handleBlur} />
                            {addForm.touched.short_description_ja && addForm.errors.short_description_ja ? <div className='text-danger'>{t(addForm.errors.short_description_ja)}</div> : null}
                        </div>
                        <div className='button-form'>
                            <button type='submit' className={'btn btn-primary w-100' + (addSubmissionLoader ? ' btn-loader' : '')}>{t('btn_submit')}</button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Sort modal */}
            <div className='modal fade sort-modal' data-bs-backdrop='static' id='sort-sub-modal' tabIndex='-1'>
                <div className='modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable'>
                    <div className='modal-content'>
                        <div className='modal-header'>
                            <h1 className='modal-title fs-5'>{t('label_sub_categories')}</h1>
                            <button type='button' className='btn-close btn-icon' onClick={handleCloseSorting} data-bs-dismiss='modal' aria-label='Close'>
                                <i className='ti ti-x'></i>
                            </button>
                        </div>
                        <div className='modal-body'>
                            {sortableList && sortableList.length ? (
                                <ReactSortable list={sortableList} setList={setSortableList} ghostClass='dropArea' handle='.dragHandle' forceFallback={true} fallbackOnBody={false} scroll={true} bubbleScroll={true} scrollSensitivity={60} filter='.ignoreDrag' preventOnFilter={true} className='grid-drag-container'>
                                    <>
                                        {sortableList.map((record, i) => (
                                            <div className={'category-box dragHandle ' + (`category_bg_${getColorType(record.color)}`)} key={i} style={{ background: record?.color }}>
                                                <div className='category-name'><span>{getTranslatedKey('name', record)}</span></div>
                                                <div className='category-item-info'><span>{record?.items_count} {t('label_items')}</span></div>
                                            </div>
                                        ))}
                                    </>
                                </ReactSortable>
                            ) : <RecordNotFound heading='no_sub_category_found_text' text='no_category_found_text_para' />}
                        </div>
                        <div className='modal-footer justify-content-center'>
                            <button type='button' className='btn btn-danger me-2 d-none' data-bs-dismiss='modal' ref={sortingCloseBtn}>Close</button>
                            <button type='button' className='btn btn-primary' disabled={!isSortingChanged()} onClick={handleSort}>{t('btn_save_changes')}</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* View offcanvas */}
            <div className='offcanvas offcanvas-end right-modal-w' id='view-sub-category' data-bs-backdrop='static' tabIndex='-1'>
                <div className='offcanvas-header'>
                    <h5 className='offcanvas-title'>{t('view_sub_category_label')}</h5>
                    <button type='button' className='btn btn-icon ms-auto' data-bs-dismiss='offcanvas' aria-label='Close'>
                        <i className='ti ti-x'></i>
                    </button>
                </div>
                <div className='offcanvas-body' onScroll={(e) => handleOffcanvasScroll(e, 'view-sub-category')}>
                    <div className='view-info'>
                        <div className='row gap-4'>
                            <div className='col-md-12'><h4>{t('label_sub_category_name_english')}:</h4><p>{editForm.values.name}</p></div>
                            <div className='col-md-12'><h4>{t('label_sub_category_name_japanese')}:</h4><p>{editForm.values.name_ja}</p></div>
                            <div className='col-md-12'>
                                <h4>{t('color_heading')}:</h4>
                                <div className='d-flex'>
                                    <span className='badge rounded-circle d-inline-block me-2' style={{ height: '20px', width: '20px', backgroundColor: editForm.values.color, border: '1px solid', borderColor: getColorType(editForm.values.color) === 'white' ? '#ccc' : editForm.values.color }}></span>
                                    <p>{editForm.values.color}</p>
                                </div>
                            </div>
                            <div className='col-md-12'><h4>{t('label_short_description')}:</h4><p>{editForm.values.short_description}</p></div>
                            <div className='col-md-12'><h4>{t('label_short_description_ja')}:</h4><p>{editForm.values.short_description_ja}</p></div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}
